import type { UIMessage } from 'ai';

/**
 * FRONT-2036 fix round 2 (N1) — extracted out of `chat-messages.tsx`, which
 * had this inline, so the phone surface (`pwa-shell.tsx`) can share the
 * exact same rule instead of re-deriving its own (and, this round, drifting
 * from it into a regression). No React, no icons, no query hooks — same
 * dependency-free-leaf precedent as `research-briefing.ts`.
 *
 * `status` flips from `'submitted'` to `'streaming'` the instant the SSE
 * connection opens — well before any token, tool call, or reasoning has
 * actually landed. A caller that only shows its "thinking" indicator while
 * `status === 'submitted'` therefore has a real gap: streaming has begun,
 * but the assistant's own message has nothing in it yet, so a surface that
 * also hides an EMPTY assistant bubble (the phone does, since FRONT-2036
 * fix round 1 item 6 — mirroring desktop's own FRONT-847 guard) shows
 * NOTHING at all for that whole window, every turn.
 *
 * This is exactly the gap desktop's `chat-messages.tsx` already closes: its
 * `isWaiting` keeps the indicator up through `'streaming'` too, for as long
 * as the last assistant message has no VISIBLE part yet (non-empty text,
 * reasoning, or a tool call — signal/data-* parts don't count, matching
 * desktop's own existing rule exactly, not a new decision made here).
 */
export function assistantHasNoVisibleContent(message: UIMessage | undefined): boolean {
  return (
    message?.role === 'assistant' &&
    !message.parts.some(
      (p) =>
        (p.type === 'text' && p.text.length > 0) ||
        p.type === 'reasoning' ||
        p.type === 'dynamic-tool' ||
        (typeof p.type === 'string' && p.type.startsWith('tool-')),
    )
  );
}
